/**
 * @author: lv.chen
 * @date: 2025/4/27
 * @desc: TagCheckersPanel组件，checkbox勾选面板
 */
import React, { useRef } from 'react';
import classnames from 'classnames';
import { isFunction } from 'lodash-es';
import { useScroll } from 'ahooks';
import { Icon, List, Checkbox, CheckChangeContext, SelectOption } from 'tea-component';
import Bubble from '@home/components/TeaComponents/Bubble';
import ConfirmableBtn from '@gp/components/ConfirmableBtn';
import { reverseCheckboxGroupValueWhenCheckChange } from '@gp/utils/select';
import { convert2StringifyValue } from './utils';
import { GROUP_KEY } from '@gp/constants/select';

import type { IBaseProps } from '@home/types/common';
import type { IRecord } from '@gp/types/global';

import './index.scss';

interface ITagCheckersPanelProps extends IBaseProps {
  searchMode?: boolean;
  deletable?: boolean | ((option: IGPSelectOption) => boolean);
  allOption?: false | SelectOption;
  groups?: IRecord<React.ReactNode>;
  options?: IGPSelectOption[] | null;
  value?: string[];
  onChange?: (payload: string[], context: CheckChangeContext | null) => void;
  onDelete?: (payload: string) => void;
  onClickCustomize?: () => void;
}

const TagCheckersPanel: React.FC<ITagCheckersPanelProps> = (props) => {
  const {
    style,
    className,
    searchMode = false,
    deletable,
    allOption = false,
    groups,
    options,
    value,
    onChange,
    onDelete,
    onClickCustomize,
  } = props;

  const hasOptions = !!options?.length;

  const allCheckboxValue = hasOptions && value?.length === options?.length;

  const checkersPanel = useRef<HTMLDivElement>(null);

  const checkersPanelScrollTop = useScroll(checkersPanel);

  const getIsDeletable = (option: IGPSelectOption): boolean => {
    if (isFunction(deletable)) return deletable(option);
    return !!deletable;
  };

  const handleAllCheckboxValueChange = (payload: boolean) => {
    const optionsValue = !payload ? [] : convert2StringifyValue(options);
    onChange?.(optionsValue, null);
  };

  const handleCheckboxGroupValueChange = (payload: string[], context: CheckChangeContext | null) => {
    const payloadTmp = reverseCheckboxGroupValueWhenCheckChange(payload, context);
    onChange?.(payloadTmp, context);
  };

  return (
    <div
      style={style}
      className={classnames('dbg-customize-tag-select-dropdown-panel', className, {
        'dbg-customize-tag-select-dropdown-panel--search': searchMode,
      })}
    >
      <div className="dbg-customize-tag-select-dropdown-panel__body">
        {!hasOptions && <div className="dbg-customize-tag-select-dropdown-panel__no-data">Sorry, no result found</div>}
        {hasOptions && (
          <div ref={checkersPanel} className="dbg-customize-tag-select-dropdown-panel__checkers">
            {!!allOption && (
              <List
                className={classnames('dbg-customize-tag-select-dropdown-panel__checkers-all-list', {
                  'is-box-shadow': !searchMode && !!checkersPanelScrollTop?.top,
                })}
                type="option"
              >
                <List.Item key={allOption.value}>
                  <Checkbox
                    indeterminate={!!value?.length && !allCheckboxValue}
                    value={allCheckboxValue}
                    onChange={handleAllCheckboxValueChange}
                  >
                    {allOption.text}
                  </Checkbox>
                </List.Item>
              </List>
            )}
            <div
              className={classnames('dbg-customize-tag-select-dropdown-panel__checkers-group-label', {
                'is-box-shadow': !!searchMode && !!checkersPanelScrollTop?.top,
              })}
            >
              {groups?.[GROUP_KEY]}
            </div>
            <Checkbox.Group layout="column" value={value} onChange={handleCheckboxGroupValueChange}>
              <List className="dbg-customize-tag-select-dropdown-panel__checkers-common-list" type="option">
                {options?.map((option) => {
                  const { value, text } = option || {};
                  const showDeleteBtn = getIsDeletable(option);
                  return (
                    <List.Item key={value}>
                      <div className="dbg-customize-tag-select-dropdown-panel__checkers-checker-context">
                        <Checkbox name={value}>{text}</Checkbox>
                        {showDeleteBtn && (
                          <ConfirmableBtn
                            confirmTitle="Do you want to delete this custom tag?"
                            confirmDescription="This action cannot be undone."
                            // @ts-ignore
                            confirmModalCfg={{
                              size: 536,
                              popupContainer: () => {
                                return document.querySelector(
                                  '.dbg-customize-tag-select-dropdown__overlay',
                                ) as HTMLElement;
                              },
                            }}
                            onConfirm={() => {
                              onDelete?.(value);
                            }}
                          >
                            <div>
                              <Bubble dark arrowPointAtCenter content="Delete">
                                <Icon
                                  className="dbg-customize-tag-select-dropdown-panel__checkers-checker-delete-btn"
                                  type="dismiss"
                                />
                              </Bubble>
                            </div>
                          </ConfirmableBtn>
                        )}
                      </div>
                    </List.Item>
                  );
                })}
              </List>
            </Checkbox.Group>
          </div>
        )}
      </div>
      {!searchMode && (
        <div className="dbg-customize-tag-select-dropdown-panel__footer">
          <span
            className="dbg-friendly-click dbg-customize-tag-select-dropdown-panel__footer-btn"
            onClick={(e) => {
              e.stopPropagation();
              onClickCustomize?.();
            }}
          >
            Customize
          </span>
        </div>
      )}
    </div>
  );
};

export default TagCheckersPanel;
